import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import useAuthPageBackground from '../hooks/useAuthPageBackground';
import styles from '../styles/LoginPage.module.css';

const ForgotPasswordPage = () => {
  useAuthPageBackground();
  const [step, setStep] = useState('identify'); // 'identify', 'confirm', 'success'
  const [identifier, setIdentifier] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [user, setUser] = useState(null);
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleIdentifyAccount = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/password-reset/identify', { identifier });

      if (response.data.success) {
        setUser(response.data.user);
        setChannels(response.data.channels);
        setStep('confirm');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to identify account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetLink = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/password-reset/initiate', {
        identifier,
        channel: selectedChannel || 'discord'
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setStep('success');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  const renderIdentifyStep = () => (
    <form onSubmit={handleIdentifyAccount} className={styles.form}>
      <div className={styles.inputGroup}>
        <label htmlFor="identifier">Username or Discord Name</label>
        <input
          type="text"
          id="identifier"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
          className={styles.input}
          disabled={isLoading}
          placeholder="Enter your username or Discord name"
        />
      </div>

      <button
        type="submit"
        className={styles.primaryButton}
        disabled={isLoading}
      >
        {isLoading ? 'Identifying...' : 'Continue'}
      </button>
    </form>
  );

  const renderConfirmStep = () => (
    <div>
      <div className={styles.successMessage}>
        <p>Account found: <strong>{user?.username}</strong></p>
        <p>Discord: <strong>{user?.discord_username}</strong></p>
      </div>

      <div className={styles.channelSelection}>
        <h3>Select Reset Method</h3>
        {channels.map(channel => (
          <div key={channel.id} className={styles.channelOption}>
            <label>
              <input
                type="radio"
                name="channel"
                value={channel.id}
                checked={selectedChannel === channel.id}
                onChange={(e) => setSelectedChannel(e.target.value)}
                disabled={!channel.available}
              />
              <span className={styles.channelName}>
                {channel.name}
                {channel.comingSoon && <span className={styles.comingSoon}> (Coming Soon)</span>}
              </span>
              {channel.value && <span className={styles.channelValue}> - {channel.value}</span>}
            </label>
          </div>
        ))}
      </div>

      <div className={styles.buttonGroup}>
        <button
          onClick={() => setStep('identify')}
          className={styles.secondaryButton}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          onClick={handleSendResetLink}
          className={styles.primaryButton}
          disabled={isLoading || !selectedChannel}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </button>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div>
      <div className={styles.successMessage}>
        <h3>✅ Reset Link Sent!</h3>
        <p>Check your Discord DMs for a message from the LOTW Admin Bot.</p>
        <p>The reset link will expire in 15 minutes.</p>
      </div>

      <div className={styles.formFooter}>
        <p>Didn't receive the message? Check your Discord server and try again.</p>
        <button
          onClick={() => {
            setStep('identify');
            setIdentifier('');
            setUser(null);
            setChannels([]);
            setError('');
            setSuccess('');
          }}
          className={styles.secondaryButton}
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <h1 className={styles.title}>Forgot Password</h1>
        <p className={styles.subtitle}>
          {step === 'identify' && 'Enter your username or Discord name to reset your password'}
          {step === 'confirm' && 'Choose how to receive your reset link'}
          {step === 'success' && 'Reset link sent successfully'}
        </p>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        {step === 'identify' && renderIdentifyStep()}
        {step === 'confirm' && renderConfirmStep()}
        {step === 'success' && renderSuccessStep()}

        <div className={styles.formFooter}>
          <p>
            <Link to="/login" className={styles.link}>Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
